import { toast, type ExternalToast } from 'sonner';
import { addDoc, collection, db, serverTimestamp } from '../firebase';
import { createLocalNotifications } from './notificationService';

export type NotificationRelatedType =
  | 'Quote'
  | 'Invoice'
  | 'Delivery'
  | 'PurchaseOrder'
  | 'Bill'
  | 'Task'
  | 'Contact'
  | 'Product';

export interface NotifyErrorOptions extends ExternalToast {
  retry?: () => void;
  detail?: string;
}

export interface BusinessNotificationInput {
  userIds: Iterable<string>;
  title: string;
  message: string;
  type: string;
  relatedId?: string;
  relatedType?: NotificationRelatedType;
  link?: string;
  severity?: 'info' | 'warning' | 'critical';
  orgId?: string;
}

const LOCAL_API_TOKEN_KEY = 'app_auth_token';
const SELECTED_ORG_ID_KEY = 'selected_org_id';

function confirm(message: string, options?: ExternalToast) {
  return toast.success(message, {
    ...options,
    duration: 2_000,
    closeButton: false,
  });
}

function error(message: string, { retry, detail, ...options }: NotifyErrorOptions = {}) {
  return toast.error(message, {
    ...options,
    duration: Infinity,
    closeButton: true,
    description: detail ?? options.description,
    action: retry ? { label: 'Retry', onClick: retry } : options.action,
  });
}

function loading(message: string, options?: ExternalToast) {
  return toast.loading(message, {
    closeButton: false,
    ...options,
  });
}

async function business({
  userIds,
  title,
  message,
  type,
  relatedId,
  relatedType,
  link,
  severity = 'info',
  orgId,
}: BusinessNotificationInput): Promise<void> {
  if (typeof window === 'undefined') return;

  const recipients = [...new Set([...userIds].filter(Boolean))];
  const resolvedOrgId = orgId || localStorage.getItem(SELECTED_ORG_ID_KEY) || '';
  if (!resolvedOrgId || recipients.length === 0) return;

  const notification = {
    title,
    message,
    type,
    relatedId,
    relatedType,
    link,
    severity,
  };

  if (localStorage.getItem(LOCAL_API_TOKEN_KEY)) {
    await createLocalNotifications(resolvedOrgId, recipients, notification);
    return;
  }

  await Promise.all(recipients.map(userId => addDoc(collection(db, 'notifications'), {
    ...notification,
    userId,
    orgId: resolvedOrgId,
    read: false,
    readAt: null,
    createdAt: serverTimestamp(),
  })));
}

export const notify = {
  confirm,
  error,
  business,
  loading,
};
